/**
 * App Component
 * Root component that sets up routing, context providers, and error handling
 */

import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { Layout } from './components/layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { GridProvider } from './contexts/GridContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { TutorialProvider, useTutorial } from './contexts/TutorialContext';
import { sessionManager } from './services/user/SessionManager';
import { Home } from './pages/Home';
import type { Composition } from './types/composition';

// Lazy load route components for code splitting
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Terms = lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard })));
const AdminCompositions = lazy(() => import('./pages/AdminCompositions').then(module => ({ default: module.AdminCompositions })));
const SharedComposition = lazy(() => import('./pages/SharedComposition').then(module => ({ default: module.SharedComposition })));
const MyCompositions = lazy(() => import('./pages/MyCompositions').then(module => ({ default: module.MyCompositions })));
const Profile = lazy(() => import('./pages/Profile').then(module => ({ default: module.Profile })));
const NotFound = lazy(() => import('./pages/NotFound').then(module => ({ default: module.NotFound })));

// Lazy load tutorial components for better initial load performance
const TutorialOverlay = lazy(() => import('./components/tutorial/TutorialOverlay').then(module => ({ default: module.TutorialOverlay })));
const TutorialErrorBoundary = lazy(() => import('./components/tutorial/TutorialErrorBoundary').then(module => ({ default: module.TutorialErrorBoundary })));

/**
 * Loading fallback component
 */
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-accent text-sm">Loading...</div>
    </div>
  );
}

/**
 * TutorialWrapper Component
 * 
 * Wraps the TutorialOverlay with an error boundary and lazy loading.
 * Provides access to tutorial context for error handling.
 * Only loads tutorial components when tutorial is active.
 */
function TutorialWrapper() {
  const { isActive, exitTutorial } = useTutorial();

  // Don't render anything if tutorial is not active (lazy loading optimization)
  if (!isActive) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <TutorialErrorBoundary onError={exitTutorial}>
        <TutorialOverlay />
      </TutorialErrorBoundary>
    </Suspense>
  );
}

/**
 * App component
 * Main application component with routing and global state management
 */
function App() {
  // State for shared composition loading
  const [sharedComposition, setSharedComposition] = useState<Composition | null>(null);
  
  // State for session initialization
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [initError, setInitError] = useState<Error | null>(null);

  /**
   * Handle shared composition loaded
   * This will be passed to the SharedComposition page
   */
  const handleCompositionLoaded = useCallback((composition: Composition) => {
    setSharedComposition(composition);
  }, []);

  /**
   * Initialize session on app mount
   * Loads authentication state and syncs user profile if authenticated
   */
  useEffect(() => {
    const initSession = async () => {
      try {
        setIsInitializing(true);
        setInitError(null);
        await sessionManager.initializeSession();
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to initialize session');
        setInitError(err);
        console.error('[App] Session initialization failed:', err);
      } finally {
        setIsInitializing(false);
      }
    };

    initSession();

    // Cleanup on unmount
    return () => {
      sessionManager.cleanup();
    };
  }, []);

  // Show loading state during session initialization
  if (isInitializing) {
    return (
      <HelmetProvider>
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center">
            <div className="text-accent text-lg mb-2">Initializing...</div>
            <div className="text-text-secondary text-sm">Loading your session</div>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  // Show error message if session initialization failed
  if (initError) {
    return (
      <HelmetProvider>
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center max-w-md px-4">
            <div className="text-red-500 text-lg mb-2">Session Initialization Failed</div>
            <div className="text-text-secondary text-sm mb-4">
              {initError.message || 'An error occurred while initializing your session'}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-accent text-background rounded hover:bg-accent-hover transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <UserProfileProvider>
              <GridProvider>
                <CreditsProvider>
                  <TutorialProvider>
                    <Layout>
                      <Suspense fallback={<LoadingFallback />}>
                        <Routes>
                        {/* Home page - not lazy loaded for faster initial render */}
                        <Route path="/" element={<Home sharedComposition={sharedComposition} />} />
                        
                        {/* About page - lazy loaded */}
                        <Route path="/about" element={<About />} />
                        
                        {/* Contact page - lazy loaded */}
                        <Route path="/contact" element={<Contact />} />
                        
                        {/* Terms page - lazy loaded */}
                        <Route path="/terms" element={<Terms />} />
                        
                        {/* Admin dashboard page - lazy loaded and protected */}
                        <Route path="/admin" element={<AdminDashboard />} />
                        
                        {/* Admin compositions page - lazy loaded and protected */}
                        <Route path="/admin/compositions" element={<AdminCompositions />} />
                        
                        {/* My Compositions page - lazy loaded and protected */}
                        <Route path="/my-compositions" element={<MyCompositions />} />
                        
                        {/* Profile page - lazy loaded and protected */}
                        <Route path="/profile" element={<Profile />} />
                        
                        {/* Shared composition page - lazy loaded */}
                        <Route 
                          path="/share/:id" 
                          element={<SharedComposition onCompositionLoaded={handleCompositionLoaded} />} 
                        />
                        
                        {/* 404 page - lazy loaded */}
                        <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </Layout>
                    
                    {/* Tutorial overlay - renders when tutorial is active */}
                    <TutorialWrapper />
                  </TutorialProvider>
                </CreditsProvider>
              </GridProvider>
            </UserProfileProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
